import type { MessageTransport } from '@speles7172/message-console';
import { apiClient } from './client';

/**
 * The seam between `@speles7172/message-console` and this application's API
 * (COMMUNICATIONS-DESIGN D6).
 *
 * ⚠️ **Hold the transport still.** Every component keys its effects on the
 * object's identity, so an object literal written inline in JSX is a new
 * transport on every render and therefore an endless reload. A module constant
 * for exactly that reason — the same rule `api/files.ts` follows, and the one
 * documented on every console in this repository.
 *
 * ⚠️ **Every method is a capability, and the omissions here are deliberate.**
 * A transport with no `postMessage` renders a read-only transcript; one with no
 * `people` renders no participant picker and no `@` autocomplete. So this
 * exports what the endpoints actually back, and nothing else: a button that
 * always fails is worse than a button that is not there.
 *
 * Not here yet, because no endpoint answers them: `threadAttachments`,
 * `templates`, `groups`. The template editor and the routing console are D-side
 * administration and land with their own endpoints; attachments on a thread are
 * a question for after the file library and the thread panel have met.
 */
export const messageTransport: MessageTransport = {
  async notifications(query) {
    const { data } = await apiClient.get('/notifications', {
      params: {
        ...(query.unreadOnly ? { unread: 'true' } : {}),
        ...(query.search ? { term: query.search } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
        ...(query.offset !== undefined ? { offset: query.offset } : {}),
      },
    });
    return data;
  },

  async unreadCount() {
    const { data } = await apiClient.get<{ count: number }>('/notifications/unread');
    return data.count;
  },

  async markRead(ids) {
    await apiClient.post('/notifications/read', { ids });
  },

  async markAllRead() {
    await apiClient.post('/notifications/read-all');
  },

  async removeNotification(id) {
    await apiClient.delete(`/notifications/${id}`);
  },

  async preferences() {
    const { data } = await apiClient.get('/notifications/preferences');
    return data;
  },

  async setPreference(change) {
    // One toggle per request, not the whole object: a page that PUTs everything
    // loses every change made in another tab.
    const { data } = await apiClient.put('/notifications/preferences', change);
    return data;
  },

  async threads(query) {
    const { data } = await apiClient.get('/threads', {
      params: {
        ...(query.entityType ? { entityType: query.entityType } : {}),
        ...(query.entityId ? { entityId: query.entityId } : {}),
        ...(query.statuses?.length ? { status: query.statuses[0] } : {}),
        ...(query.search ? { term: query.search } : {}),
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
        ...(query.offset !== undefined ? { offset: query.offset } : {}),
      },
    });
    return data;
  },

  async thread(threadId) {
    const { data } = await apiClient.get(`/threads/${threadId}`);
    return data;
  },

  async startThread(input) {
    const { data } = await apiClient.post('/threads', input);
    return data.thread ?? data;
  },

  async postMessage({ threadId, body }) {
    const { data } = await apiClient.post(`/threads/${threadId}/messages`, { body });
    return data;
  },

  async setThreadStatus({ threadId, status }) {
    const { data } = await apiClient.put(`/threads/${threadId}/status`, { status });
    return data;
  },

  async addParticipants({ threadId, recipientIds }) {
    await apiClient.post(`/threads/${threadId}/participants`, { recipientIds });
  },

  async markThreadRead(threadId) {
    await apiClient.post(`/threads/${threadId}/read`);
  },

  /**
   * Who can be added to a conversation.
   *
   * ⚠️ **Its presence is what draws the participant picker and the `@`
   * autocomplete.** Without it the thread panel renders neither — which would
   * leave a conversation nobody can add anyone to.
   */
  people: {
    async search(query) {
      const { data } = await apiClient.get('/options/people', {
        params: {
          ...(query.term ? { term: query.term } : {}),
          ...(query.limit !== undefined ? { limit: query.limit } : {}),
          ...(query.offset !== undefined ? { offset: query.offset } : {}),
        },
      });
      return data;
    },

    async resolve(values) {
      const { data } = await apiClient.get('/options/people/resolve', {
        params: { values: values.join(',') },
      });
      return data.options;
    },
  },
};

/**
 * The conversation a record already has, if any.
 *
 * ⚠️ **Not `threads({ entityType, entityId })`.** That list is scoped to the
 * caller's own memberships, so a producer left off an enquiry's participants
 * sees nothing — and a caller that reads "nothing" as "start one" opens a
 * second thread on an enquiry already being answered. This asks by record.
 */
export async function fetchRecordThreadId(
  entityType: string,
  entityId: string,
): Promise<string | null> {
  const { data } = await apiClient.get('/threads/for-record', {
    params: { entityType, entityId },
  });
  return data?.threadId ?? null;
}

/** How many threads each of these records has, for a list page. */
/** What the endpoint will answer about in one call. */
const COUNTS_PER_REQUEST = 200;

export async function fetchThreadCounts(
  entityType: string,
  entityIds: string[],
): Promise<Record<string, { total: number; unread: number }>> {
  if (entityIds.length === 0) return {};

  /*
   * ⚠️ **Chunked, not truncated.** The endpoint answers about 200 records at a
   * time, and an earlier version simply sliced to the first 200 — so on a
   * longer list every later record was reported as having no conversation,
   * which is indistinguishable from having none. A caller cannot tell a
   * silently dropped id from a genuine zero, so this does not create any.
   */
  const chunks: string[][] = [];
  for (let i = 0; i < entityIds.length; i += COUNTS_PER_REQUEST) {
    chunks.push(entityIds.slice(i, i + COUNTS_PER_REQUEST));
  }

  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const { data } = await apiClient.get('/threads/counts', {
        params: { entityType, entityIds: chunk.join(',') },
      });
      return data as Record<string, { total: number; unread: number }>;
    }),
  );

  return Object.assign({}, ...pages);
}
